import React, { useState } from 'react';
import { components, OptionsType } from 'react-select';
import Async from 'react-select/async';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import { Icon, Translate } from '@hydra/common/components';

import { doSerpApiGetGeoLocationsRequest } from '@hydra/utils/serpApiRequest';
import { getCountryCapital } from '@hydra/utils/getCountryCapital';

import { useI18n } from '@hydra/common/customHooks';
import { SerpGeoLocation } from '@hydra/types/serp';

const useStyles = makeStyles(theme => ({
  icon: {
    cursor: 'pointer',
    marginLeft: theme.spacing(0.5),
    display: 'inline'
  },
  localIcon: {
    verticalAlign: 'sub',
    display: 'inline',
    marginRight: theme.spacing(2)
  },
  title: {
    fontSize: theme.typography.fontSizeLarge,
    fontWeight: theme.typography.fontWeightSemibold
  },
  titleContainer: {
    marginBottom: theme.spacing(0.75),
    cursor: 'pointer'
  }
}));

const geoSelectStyles = (theme: Theme) => ({
  menu: (base: any) => ({
    ...base,
    zIndex: theme.zIndex.appBar
  })
});

const useDisplayLocation = (data: {nameLabel?: string, typeLabel: string}, emptyLabel?: string) => {
  const classes = useStyles();
  return (
    <>
      <div className={classes.localIcon}>
        <Icon type='local' color='grey' size='sm' />
      </div>
      <span>
        {data && data.nameLabel ? `${data.nameLabel} - ${data.typeLabel}` : emptyLabel}
      </span>
    </>
  );
};

const Option = ({ data, ...props }: any) => {
  return (
    <components.Option {...props}>
      {useDisplayLocation(data)}
    </components.Option>
  );
};

const SingleValue = ({ data, emptyLabel, ...props }: any) => {
  return (
    <components.SingleValue {...props}>
      {useDisplayLocation(data, emptyLabel)}
    </components.SingleValue>
  );
};

const isOptionSelected = ({ locName, locType, nameLabel, typeLabel }: SerpGeoLocation, [loc]: OptionsType<SerpGeoLocation>) => {
  return loc.locName === locName &&
    loc.locType === locType &&
    loc.nameLabel === nameLabel &&
    loc.typeLabel === typeLabel;
};

const fetchOptions = async ({ location, callback, country, translate }: {
  location: string,
  country: string,
  callback: (value: SerpGeoLocation[]) => void,
  translate: (value: string) => string
}) => {
  const qs = { country, location: location.toLowerCase() };
  const values = await doSerpApiGetGeoLocationsRequest(qs);
  return callback(
    /* eslint-disable camelcase */
    values.map(({ loc_type: locType, loc_name_canonical: locNameCanonical }: {loc_type: string, loc_name_canonical: string}) => {
      /* eslint-enable camelcase */
      const splittedLocNames = locNameCanonical.split(',');
      const typeKey = locType.split(' ').map(word => word.toLowerCase()).join('_');
      return {
        locName: locNameCanonical,
        locType,
        nameLabel: splittedLocNames.filter((_, index) => index !== splittedLocNames.length - 1).join(', '),
        typeLabel: translate(typeKey)
      };
    })
  );
};

const debouncedFetchOptions = debounce(fetchOptions, 250);

const getOptions = (props: any) => {
  debouncedFetchOptions(props);
};

interface SelectGeoLocationProps {
  country?: string
  selectedGeoLocation: SerpGeoLocation
  selectGeoLocation: (value: any) => void
}

export default function SelectGeoLocation ({ country, selectGeoLocation, selectedGeoLocation }: SelectGeoLocationProps) {
  const classes = useStyles();
  const theme = useTheme();
  const { translate } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const capitalCity = getCountryCapital(country);

  return (
    <>
      <div
        className={classes.titleContainer}
        onClick={() => isEmpty(selectedGeoLocation) ? setExpanded(!expanded) : null}
      >
        <Translate content='serp_add_geolocation' className={classes.title} />
        <Icon
          type={expanded ? 'chevron-up' : 'chevron-down'}
          size='sm'
          className={classes.icon}
        />
      </div>
      {expanded
        ? (
          <Async
            components={{
              Option,
              SingleValue: props => SingleValue({
                emptyLabel: `${translate('serp_add_geolocation_example')}: '${capitalCity}'`,
                ...props
              })
            }}
            isClearable
            styles={geoSelectStyles(theme)}
            onChange={selectGeoLocation}
            loadOptions={(location, callback) => getOptions({ location, callback, country, translate })}
            value={selectedGeoLocation}
            isOptionSelected={isOptionSelected}
            loadingMessage={() => translate('loading')}
            noOptionsMessage={() => translate('no_options_localisation')}
          />
          )
        : null}
    </>
  );
}
